import { roleIn } from '../../utils/roleGrants';
import {
  AttemptState,
  AutoRechargeSettingsBody,
  AutoRechargeStatus,
  GatewayMode,
  GatewayType,
  MandateMethod,
  MandateStatus,
} from '../../types/autorecharge';

/**
 * Pure rules for the auto-recharge panel. The server decides everything again; these
 * only keep the form honest and the words consistent.
 */

/**
 * Backend CONFIGURE list (AutoRechargeController), lower-cased as the portal holds roles:
 * owners and above only (owner decision, 2026-10-25). A business's admins (OPERATOR_ADMIN,
 * which IS BUSINESS_ADMIN under its old name) may only read. Spellings are paired by roleIn.
 */
export const AUTORECHARGE_CONFIGURE_ROLES = [
  'platform_admin',
  'master_admin',
  'super_admin',
  'service_admin',
  'provider_owner',
  'provider_admin',
  'business_owner',
];

/** Owners and above, either spelling. Reach (which organisations) is the server's: it answers 404. */
export const canConfigureAutoRecharge = (role: string | null | undefined): boolean =>
  roleIn(role, AUTORECHARGE_CONFIGURE_ROLES);

/** Master only (backend checks MASTER_ADMIN). */
export const canManageGateways = (role: string | null | undefined): boolean =>
  (role || '').toLowerCase() === 'master_admin';

export const gatewayNames: Record<GatewayType, string> = {
  RAZORPAY: 'Razorpay',
  STRIPE: 'Stripe',
};

export const methodLabels: Record<MandateMethod, string> = {
  UPI: 'UPI AutoPay',
  CARD: 'Card',
  EMANDATE: 'Netbanking e-mandate',
};

export const setupButtonLabel = (g: GatewayType): string =>
  g === 'RAZORPAY' ? 'Set up autopay with Razorpay' : 'Save a card with Stripe';

export const modeLabel = (m: GatewayMode): string =>
  m === 'LIVE' ? 'Live' : m === 'TEST' ? 'Test mode — no real money moves' : 'Off';

export const mandateStatusLabels: Record<MandateStatus, string> = {
  PENDING: 'Waiting for the customer to authorise',
  ACTIVE: 'Active',
  PAUSED: 'Paused by the customer',
  REJECTED: 'Rejected',
  REVOKED: 'Revoked',
  EXPIRED: 'Expired',
};

export const mandateStatusClass = (s: MandateStatus): string =>
  s === 'ACTIVE'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    : s === 'PENDING' || s === 'PAUSED'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

export const attemptStateLabels: Record<AttemptState, string> = {
  PLANNED: 'Starting',
  NOTIFIED: 'Pre-debit notice sent',
  CHARGING: 'Debit requested',
  CAPTURED: 'Credited',
  FAILED: 'Failed',
  ACTION_REQUIRED: 'Customer must authenticate',
  EXPIRED: 'Expired (no result)',
  CANCELLED: 'Cancelled',
};

export const attemptStateClass = (s: AttemptState): string =>
  s === 'CAPTURED'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    : s === 'FAILED' || s === 'ACTION_REQUIRED' || s === 'EXPIRED'
      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      : s === 'CANCELLED'
        ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
        : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';

/** The form as typed: strings, '' = empty. */
export interface AutoRechargeForm {
  enabled: boolean;
  threshold: string;
  amount: string;
  maxPerDebit: string;
  dailyCap: string;
  maxAttemptsPerDay: string;
  cooldownMinutes: string;
}

export const formFromStatus = (s: AutoRechargeStatus): AutoRechargeForm => ({
  enabled: s.enabled,
  threshold: s.threshold === null || s.threshold === undefined ? '' : String(s.threshold),
  amount: s.amount === null || s.amount === undefined ? '' : String(s.amount),
  maxPerDebit: s.maxPerDebit === null || s.maxPerDebit === undefined ? '' : String(s.maxPerDebit),
  dailyCap: s.dailyCap === null || s.dailyCap === undefined ? '' : String(s.dailyCap),
  maxAttemptsPerDay: String(s.maxAttemptsPerDay ?? 2),
  cooldownMinutes: String(s.cooldownMinutes ?? 60),
});

const MONEY = /^\d+(\.\d{1,2})?$/;
const INT = /^\d+$/;

/** null when fine, else what to fix. Mirrors the server's 400s. */
export const validateForm = (f: AutoRechargeForm): string | null => {
  if (f.threshold !== '' && !MONEY.test(f.threshold.trim())) return 'Threshold must be an amount (≥ 0, up to 2 decimals)';
  if (f.amount !== '' && (!MONEY.test(f.amount.trim()) || Number(f.amount) <= 0)) {
    return 'Recharge amount must be more than 0 (up to 2 decimals)';
  }
  if (f.maxPerDebit !== '' && (!MONEY.test(f.maxPerDebit.trim()) || Number(f.maxPerDebit) <= 0)) {
    return 'Max per debit must be more than 0';
  }
  if (f.dailyCap !== '' && (!MONEY.test(f.dailyCap.trim()) || Number(f.dailyCap) <= 0)) return 'Daily cap must be more than 0';
  if (!INT.test(f.maxAttemptsPerDay) || Number(f.maxAttemptsPerDay) < 1 || Number(f.maxAttemptsPerDay) > 10) {
    return 'Attempts per day must be 1–10';
  }
  if (!INT.test(f.cooldownMinutes) || Number(f.cooldownMinutes) > 10080) return 'Cooldown must be 0–10080 minutes';
  if (f.enabled && (f.threshold === '' || f.amount === '')) return 'Set the threshold and the recharge amount to turn it on';
  if (f.dailyCap !== '' && f.amount !== '' && Number(f.dailyCap) < Number(f.amount)) {
    return 'The daily cap is below one recharge';
  }
  return null;
};

/** The PUT body: every field present; a blank cap clears it. */
export const settingsBody = (f: AutoRechargeForm): AutoRechargeSettingsBody => {
  const body: AutoRechargeSettingsBody = {
    enabled: f.enabled,
    maxAttemptsPerDay: Number(f.maxAttemptsPerDay),
    cooldownMinutes: Number(f.cooldownMinutes),
  };
  if (f.threshold !== '') body.threshold = Number(f.threshold);
  if (f.amount !== '') body.amount = Number(f.amount);
  if (f.maxPerDebit !== '') body.maxPerDebit = Number(f.maxPerDebit);
  else body.clearMaxPerDebit = true;
  if (f.dailyCap !== '') body.dailyCap = Number(f.dailyCap);
  else body.clearDailyCap = true;
  return body;
};

/**
 * What the customer is charged for one recharge, tax included, from the server's
 * per-recharge figures for the SAVED amount, rescaled to the typed amount.
 */
export const estimateCharge = (amount: string, s: AutoRechargeStatus): number | null => {
  if (!MONEY.test(amount.trim())) return null;
  const a = Number(amount);
  const rate = s.perRecharge ? Number(s.perRecharge.rate) : s.gateway.gateway === 'RAZORPAY' ? 18 : 0;
  return Math.round(a * (1 + rate / 100) * 100) / 100;
};

/** Suggested mandate maximum: the charge with headroom, within Razorpay's ₹99,999. */
export const suggestedMandateMax = (charge: number | null, gateway: GatewayType): string => {
  if (charge === null || charge <= 0) return '';
  const v = Math.ceil(charge * 1.5);
  return String(gateway === 'RAZORPAY' ? Math.min(v, 99999) : v);
};

export const validateMandateMax = (max: string, gateway: GatewayType, charge: number | null): string | null => {
  if (gateway === 'STRIPE' && max.trim() === '') return null;
  if (!MONEY.test(max.trim()) || Number(max) <= 0) return 'Enter the most one debit may be (tax included)';
  if (gateway === 'RAZORPAY' && (Number(max) < 1 || Number(max) > 99999)) return 'Between ₹1 and ₹99,999';
  if (charge !== null && Number(max) < charge) return `Below one recharge (${charge.toFixed(2)} incl. tax): debits would be refused`;
  return null;
};

/** Where Stripe Checkout returns the customer. Must be https; locally there is none. */
export const returnUrl = (origin: string, path = '/billing/accounts'): string | undefined =>
  origin.startsWith('https://') ? origin + path : undefined;

/** Payment gateway account form: SSM names only, under the platform's prefix. */
export const SSM_PREFIX = '/cpaas/prod/payments/';

export const validateParamName = (name: string, required: boolean): string | null => {
  const n = name.trim();
  if (!n) return required ? 'Required' : null;
  if (/^(rzp_|sk_|rk_|whsec_)/.test(n)) return 'Enter the SSM parameter NAME, never the key itself';
  if (!n.startsWith(SSM_PREFIX)) return `Must start with ${SSM_PREFIX}`;
  if (n.includes('..')) return 'Must not contain ..';
  return null;
};

/** India ↔ Razorpay, everyone else ↔ Stripe: the same rule the server enforces. */
export const validateMarket = (gateway: GatewayType, countries: string, currencies: string): string | null => {
  const cs = countries.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean);
  const cur = currencies.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean);
  if (cs.length === 0 || cur.length === 0) return 'Countries and currencies are required';
  if (gateway === 'RAZORPAY') {
    return cs.length === 1 && cs[0] === 'IN' && cur.length === 1 && cur[0] === 'INR'
      ? null
      : 'A Razorpay account serves country IN and currency INR only';
  }
  return cs.includes('IN') || cur.includes('INR') ? 'A Stripe account cannot serve India or INR (those use Razorpay)' : null;
};
